import { type FC, useMemo } from "react";
import { CheckCircle2, MinusCircle, Plus } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Progress } from "@/shared/components/ui/progress";
import { KEY_DOCUMENT_SLOTS } from "@/shared/constants/keyDocuments";
import {
  getAuthorityDocumentName,
  getAuthorityDocumentType,
} from "@/shared/constants/legalAuthorityDocuments";
import { useProbateAnswers } from "@/shared/hooks/useWillAnswer";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { cn } from "@/shared/utils/core";
import type { DocumentType } from "@/shared/types/documentTypes";

interface KeyDocumentsCardProps {
  documents: { document_type: DocumentType }[];
  onAddDocument: (documentType: DocumentType, label: string) => void;
  className?: string;
}

export const KeyDocumentsCard: FC<KeyDocumentsCardProps> = ({
  documents,
  onAddDocument,
  className,
}) => {
  const { person } = useAppContext();
  const { hasWill, hasTrust, probateState } = useProbateAnswers(person?.estate_id);

  const rows = useMemo(() => {
    const uploadedTypes = new Set(documents.map((doc) => doc.document_type));
    const notApplicableSlots = new Set(
      [hasWill === "no" ? "will" : null, hasTrust === "no-trust" ? "trust" : null].filter(Boolean),
    );

    return KEY_DOCUMENT_SLOTS.map((slot) => {
      const isUploaded = slot.documentTypes.some((type) => uploadedTypes.has(type));
      return {
        ...slot,
        label: slot.id === "letters" ? getAuthorityDocumentName(probateState, hasWill) : slot.label,
        uploadType:
          slot.id === "letters"
            ? (getAuthorityDocumentType(hasWill) as DocumentType)
            : slot.documentTypes[0],
        isUploaded,
        isNotApplicable: !isUploaded && notApplicableSlots.has(slot.id),
      };
    }).sort((a, b) => Number(b.isUploaded) - Number(a.isUploaded));
  }, [documents, probateState, hasWill, hasTrust]);

  const applicableRows = rows.filter((row) => !row.isNotApplicable);
  const uploadedCount = applicableRows.filter((row) => row.isUploaded).length;
  const totalCount = applicableRows.length;

  return (
    <div className={cn("rounded-xl border border-primary bg-white p-5", className)}>
      <h4 className="text-base font-medium">Key documents</h4>
      <p className="text-secondary text-xs mt-1">
        A quick checklist of the most important files you need.
      </p>

      <div className="flex items-center gap-3 mt-4">
        <Progress
          value={totalCount > 0 ? (uploadedCount / totalCount) * 100 : 0}
          className="flex-1"
          indicatorClassName="bg-brand"
          aria-label={`${uploadedCount} of ${totalCount} key documents uploaded`}
        />
        <span className="text-secondary text-sm flex-shrink-0">
          {uploadedCount} of {totalCount}
        </span>
      </div>

      <div className="flex flex-col mt-4">
        {rows.map((row) => (
          <div
            key={row.id}
            className={cn(
              "flex items-center gap-3",
              row.isUploaded || row.isNotApplicable ? "min-h-8" : "min-h-11",
            )}
          >
            {row.isUploaded ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-brand flex-shrink-0" aria-hidden="true" />
                <span className="text-sm text-tertiary flex-1 min-w-0">{row.label}</span>
              </>
            ) : row.isNotApplicable ? (
              <>
                <MinusCircle className="h-4 w-4 text-disabled flex-shrink-0" aria-hidden="true" />
                <span className="text-sm text-disabled line-through flex-1 min-w-0">
                  {row.label}
                </span>
                <span className="text-xs text-tertiary flex-shrink-0">
                  {row.notApplicableLabel}
                </span>
              </>
            ) : (
              <>
                <span className="text-sm flex-1 min-w-0">{row.label}</span>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => onAddDocument(row.uploadType, row.label)}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Add
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
